import { Box, Button, Grid, Typography } from '@mui/material';
import get from 'lodash-es/get';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { contractFormText } from '../../constants/contracts';
import { useConstants } from '../../hooks/useConstants';
import Checkbox from '../forms/Checkbox';
import FormSection from '../forms/FormSection';
import FormSubsection from '../forms/FormSubsection';
import S3Upload from '../forms/S3Upload';
import Select from '../forms/Select';
import TextField from '../forms/TextField';
import BreakingFlex from '../shared/BreakingFlex';
import BudgetRatesFields from './BudgetRatesFields';

function ContractBudgetRatesSection() {
  const { constants } = useConstants();
  const STAGE = get(constants, 'RAILS.STAGE');

  const { getValues, setValue } = useFormContext();

  const form = useFormContext();
  const { resetField, watch } = form;

  const [frequencyCapEnabled, performanceCapEnabled] = watch(['frequencyCapEnabled', 'performanceCapEnabled']);

  // values extracted from formState
  const [contractId, bidType] = getValues(['contractId', 'bidType']);
  const isNewContract = Boolean(contractId) === false;

  const generateKey = () => `${STAGE}/attribution_targets/${isNewContract ? 'tmp' : `contract=${contractId}`}`;

  const handleFrequencyCapEnabledChange = (e) => {
    if (!e.target.checked) {
      resetField('maxUserViewsPerPeriod');
      resetField('frequencyCapping');
    }
    return e.target.checked;
  };
  const handlePerformanceCapEnabledChange = (e) => {
    if (!e.target.checked) {
      resetField('maxUserClicksPerPeriod');
      resetField('clickFrequencyCapping');
    }
    return e.target.checked;
  };

  return (
    <FormSection title={contractFormText.sectionTitles.budgetRates}>
      <BudgetRatesFields bidType={bidType}>
        <Grid item xs={4}>
          <Select
            fullWidth
            name="bidType"
            label="Contract Type"
            options={[
              { value: 'CPM', label: 'CPM' },
              { value: 'CPC', label: 'CPC' },
              { value: 'CPA', label: 'CPA' },
              { value: 'CPV', label: 'CPV' },
            ]}
          />
        </Grid>
        <Grid item xs={4}>
          <Checkbox name="multiRate" label="Multi Rate" />
          <Checkbox name="costPlus" label="Enable Cost Plus" />
        </Grid>
      </BudgetRatesFields>
      <FormSubsection title={contractFormText.subsectionTitles.attribution}>
        <BreakingFlex breakpoint="md">
          <S3Upload
            s3InfoUrl="/admin/helpers/s3_upload_info/ventura"
            generateKey={generateKey}
            preserveName
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            label={contractFormText.attributionUpload}
            name="attributionFile"
            sx={{ height: 'auto' }}
            onSuccess={(linksParam, pathParam) =>
              setValue('attributionFile', pathParam, { shouldValidate: true, shouldDirty: true })
            }
          />
          <Button onClick={() => window.open(contractFormText.locationsTemplateLink, '_blank')}>
            {contractFormText.attributionDownload}
          </Button>
        </BreakingFlex>
      </FormSubsection>
      <FormSubsection title={contractFormText.subsectionTitles.cap}>
        <BreakingFlex breakpoint="md">
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, pb: 3, pl: 2 }}>
            <Checkbox
              name="frequencyCapEnabled"
              label="Enable Frequency Cap"
              normalizeOutput={handleFrequencyCapEnabledChange}
            />
          </Box>
          {frequencyCapEnabled && (
            <>
              <TextField
                label="Max Impressions"
                name="maxUserViewsPerPeriod"
                disabled={!frequencyCapEnabled}
                type="number"
              />
              <Select
                disabled={!frequencyCapEnabled}
                name="frequencyCapping"
                label="Frequency"
                sx={{ minWidth: 225 }}
                options={[
                  { value: 'daily', label: 'Per Day' },
                  { value: 'weekly', label: 'Per Week' },
                  { value: 'monthly', label: 'Per Month' },
                ]}
              />
            </>
          )}
        </BreakingFlex>
        <Typography variant="body2" sx={{ mb: 2, color: '#616161' }}>
          {`(Setting a low frequency cap might impact the campaign's delivery. We recommend applying a daily cap of at
              least 3 impressions.)`}
        </Typography>
        <BreakingFlex breakpoint="md">
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, pb: 3, pl: 2 }}>
            <Checkbox
              name="performanceCapEnabled"
              label="Enable Performance Cap"
              normalizeOutput={handlePerformanceCapEnabledChange}
            />
          </Box>
          {performanceCapEnabled && (
            <>
              <TextField
                label="Max Impressions"
                name="maxUserClicksPerPeriod"
                disabled={!performanceCapEnabled}
                type="number"
              />
              <Select
                disabled={!performanceCapEnabled}
                name="clickFrequencyCapping"
                label="Frequency"
                sx={{ minWidth: 225 }}
                options={[
                  { value: 'daily', label: 'Per Day' },
                  { value: 'weekly', label: 'Per Week' },
                ]}
              />
            </>
          )}
        </BreakingFlex>
      </FormSubsection>
    </FormSection>
  );
}

export default ContractBudgetRatesSection;
